import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Zap } from "lucide-react";

const CONTENT = {
    terms: {
        title: "Terms & Conditions",
        updated: "Last updated: July 2026",
        sections: [
            { h: "1. About BettingNator", p: "BettingNator provides football betting analysis and daily double selections for informational and subscription purposes. By accessing this website or purchasing a subscription, you agree to these Terms & Conditions." },
            { h: "2. Eligibility", p: "You must be at least 18 years old (or the legal gambling age in your jurisdiction) to use this website and its services. By using BettingNator you confirm that you meet this requirement and that online betting content is legal where you live." },
            { h: "3. Nature of the Service", p: "All selections, doubles and analysis published by BettingNator are opinions based on research and statistics. They are provided for informational purposes only and do not constitute financial advice. We do not operate a bookmaker and we never accept or place bets on behalf of users." },
            { h: "4. No Guaranteed Outcomes", p: "Sports results are inherently uncertain. BettingNator does not guarantee winnings, profit or any specific outcome. Past results displayed in our records archive do not guarantee future results." },
            { h: "5. Subscriptions & Payments", p: "Subscription plans grant access to daily singles or daily doubles, depending on the plan purchased. Prices are listed in EUR and confirmed before payment. Subscriptions are personal and may not be shared or resold." },
            { h: "6. Acceptable Use", p: "You agree not to copy, redistribute or resell BettingNator content, and not to use the website for any unlawful purpose." },
            { h: "7. Limitation of Liability", p: "To the maximum extent permitted by law, BettingNator is not liable for any losses — financial or otherwise — arising from the use of our analysis or selections. You are solely responsible for your betting decisions." },
            { h: "8. Changes", p: "We may update these terms from time to time. Continued use of the website after changes are published constitutes acceptance of the updated terms." },
        ],
    },
    privacy: {
        title: "Privacy Policy",
        updated: "Last updated: July 2026",
        sections: [
            { h: "1. What We Collect", p: "When you use our contact form we collect your name, email address, subject and message. If you subscribe, we collect the details needed to deliver your plan. We also use cookies to remember preferences and understand site usage." },
            { h: "2. Cookies", p: "We use a small number of cookies: essential cookies required for the site to function, and preference cookies (for example, remembering your cookie consent choice). You can decline cookies in your browser settings, though some features may not work as intended." },
            { h: "3. How We Use Your Data", p: "Your data is used solely to respond to enquiries, deliver subscriptions and improve the website. We do not sell, rent or share your personal data with third parties for marketing purposes." },
            { h: "4. Data Retention", p: "Contact messages are kept only as long as needed to handle your enquiry. You may request deletion of your personal data at any time by emailing bettingnator.info@gmail.com." },
            { h: "5. Security", p: "We apply reasonable technical and organisational measures to protect your information. However, no method of transmission over the internet is completely secure." },
            { h: "6. Your Rights", p: "Depending on your jurisdiction, you may have the right to access, correct or delete your personal data, and to object to or restrict its processing. Contact us at bettingnator.info@gmail.com to exercise these rights." },
            { h: "7. Contact", p: "For any privacy questions, email bettingnator.info@gmail.com." },
        ],
    },
    disclaimer: {
        title: "Disclaimer",
        updated: "Last updated: July 2026",
        sections: [
            { h: "1. Informational Content Only", p: "All content on BettingNator — including daily doubles, analysis and historical records — is provided for informational and entertainment purposes only. It does not constitute financial, investment or gambling advice." },
            { h: "2. Risk Warning", p: "Betting involves significant financial risk and can be addictive. You may lose some or all of the money you stake. Never bet more than you can afford to lose, and never chase losses." },
            { h: "3. No Guarantees", p: "Past results do not guarantee future results. Nothing on this website should be interpreted as a promise of winnings, guaranteed profit or risk-free betting." },
            { h: "4. Accuracy", p: "We work hard to keep records and information accurate, but we make no warranty that all content is complete, current or error-free. Odds shown are historical and will differ from live bookmaker odds." },
            { h: "5. Third-Party Bookmakers", p: "BettingNator is independent and is not affiliated with any bookmaker. Any bets you place are agreements between you and your chosen operator, under their terms." },
            { h: "6. Responsible Gambling", p: "If betting stops being fun or you feel it is becoming a problem, seek help. Support is available from organisations such as BeGambleAware and GamCare. 18+ only." },
        ],
    },
};

export default function LegalPage({ page }) {
    const doc = CONTENT[page];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [page]);

    return (
        <main className="min-h-screen">
            <header className="fixed top-0 inset-x-0 z-50 bg-[#05070A]/85 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between">
                    <Link to="/" data-testid="legal-brand-logo" className="flex items-center gap-2">
                        <span className="w-8 h-8 rounded-lg bg-bn-blue/15 border border-bn-blue/40 flex items-center justify-center">
                            <Zap className="w-4 h-4 text-bn-blue" />
                        </span>
                        <span className="font-display font-extrabold tracking-tight text-lg text-white">
                            BETTING<span className="text-bn-blue">NATOR</span>
                        </span>
                    </Link>
                    <Link
                        to="/"
                        data-testid="legal-back-home-button"
                        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to site
                    </Link>
                </div>
            </header>

            <article data-testid="legal-content-page" className="max-w-4xl mx-auto px-4 sm:px-6 pt-36 pb-24">
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                    <div className="font-mono text-[11px] tracking-[0.3em] text-bn-blue font-semibold mb-4">LEGAL</div>
                    <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-white">{doc.title}</h1>
                    <p className="mt-3 font-mono text-xs text-slate-500">{doc.updated}</p>
                </motion.div>

                <div className="mt-12 space-y-10">
                    {doc.sections.map((s, i) => (
                        <motion.section
                            key={s.h}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5, delay: i * 0.04 }}
                            className="border-l-2 border-bn-blue/40 pl-6"
                        >
                            <h2 className="font-display font-bold text-xl text-white">{s.h}</h2>
                            <p className="mt-3 text-base text-slate-400 leading-relaxed">{s.p}</p>
                        </motion.section>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    className="mt-16 rounded-xl border border-bn-line bg-bn-card/50 p-5"
                >
                    <p className="text-xs text-slate-500 leading-relaxed">
                        Betting involves financial risk. Past results do not guarantee future results. 18+ only — please bet responsibly. Questions? Email bettingnator.info@gmail.com.
                    </p>
                </motion.div>
            </article>
        </main>
    );
}
